import React, { useMemo } from 'react';
import {
  ReportColumn,
  DEFAULT_SHOW,
  ReportFilter,
  ACTION_COLUMN,
} from 'components/modules/report/report-types';
import { Model } from 'util/backendapi/models/api.interfaces';
import { t, Trans } from '@lingui/macro';
import PageStandard from 'components/modules/pagestandard/pagestandard';
import { ReportFiltersBlock } from 'components/modules/report/filter/ReportFiltersBlock';
import ActionBlock from 'components/base/actionblock/actionblock';
import { ReportTable } from 'components/modules/report/table/ReportTable';
import ButtonShowModal from 'components/base/modal/buttonshowmodal';
import { EditAlarmCategoryModal } from './EditAlarmCategoryModal';
import { ReportStateProps } from 'hooks/use-report-state';
import { ButtonShowConfirmation } from 'components/base/confirmation/ButtonShowConfirmation';

export interface AlarmCategoryListViewProps {
  reportProps: ReportStateProps<Model.ReportsAlarmCategory>;
  retireAlarmCategory: (id: number) => Promise<any>;
}

export const alarmCategoryListColumns: (props: {
  refreshList: () => void;
  retireAlarmCategory: (id: number) => Promise<any>;
}) => ReportColumn<Model.ReportsAlarmCategory>[] = ({
  refreshList,
  retireAlarmCategory,
}) => [
  {
    label: <Trans>Name</Trans>,
    name: 'name',
    visibility: DEFAULT_SHOW,
    accessor: ({ name }) => name,
  },
  {
    ...ACTION_COLUMN,
    additionalFields: ['id', 'name'],
    accessor: ({ id, name }) => (
      <>
        <ButtonShowConfirmation
          name="retire-alarm-category"
          title={t`Retire alarm category`}
          iconType="icon-circle-minus"
          className="btn-link-panel"
          content={
            <Trans>
              Are you sure you want to retire the <strong>{name}</strong> alarm
              category? Once retired the alarm category will not be available to
              assign to any alarm reports. This action is not reversible.
            </Trans>
          }
          okBtnText={<Trans>Yes, retire</Trans>}
          onConfirm={async function () {
            await retireAlarmCategory(id!);
            refreshList();
          }}
        />
      </>
    ),
  },
];

export const alarmCategoryListFilters: ReportFilter[] = [];

export function AlarmCategoryListView(props: AlarmCategoryListViewProps) {
  const { reportProps, retireAlarmCategory } = props;

  const filters = alarmCategoryListFilters;

  const columns = useMemo(
    () =>
      alarmCategoryListColumns({
        retireAlarmCategory,
        refreshList: reportProps.refreshList,
      }),
    [retireAlarmCategory, reportProps.refreshList]
  );

  return (
    <PageStandard
      name="alarm-categories-report"
      header={<Trans>Maintain alarm categories</Trans>}
    >
      <div className="page-content-header-filters-actions">
        <ReportFiltersBlock
          filtersBackend={reportProps.reportInfo?.filters}
          filtersFrontend={filters}
          isExportMode={reportProps.isExportMode}
        />
      </div>
      <div className="filtered-table-wrapper">
        <ActionBlock className="text-right">
          <ButtonShowModal
            name="create-alarm-category"
            shortcut="CREATE_NEW"
            iconType="icon-plus"
            modalContent={({ hideModal }) => (
              <EditAlarmCategoryModal
                onAfterSubmit={() => {
                  hideModal();
                  reportProps.refreshList();
                }}
              />
            )}
          >
            <Trans>Create alarm category</Trans>
          </ButtonShowModal>
        </ActionBlock>
        <ReportTable<Model.ReportsAlarmCategory>
          {...reportProps}
          columns={columns}
          filters={filters}
        />
      </div>
    </PageStandard>
  );
}
